import React, { useContext } from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import Link from "next/link";
import PropTypes from "prop-types";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import breakpoints from "../../constants";
import { hoursTypeMap } from "../../helpers/vacancyHelpers";
import Loader from "../Loader/Loader";

const StyledImageWrapper = styled.div`
  width: 140px;
`;

const StyledVacancy = styled.a`
  display: block;
  border: 1px solid black;
  color: inherit;
  text-decoration: none;

  padding-top: ${({ $withoutImage }) => ($withoutImage ? "140px" : "")};

  @media (min-width: ${breakpoints.TABLET}) {
    min-height: 450px;
  }

  .vacancy-text-section {
    background-color: transparent;
    transition: background-color 0.5s ease;
  }

  &:hover {
    cursor: pointer;

    .vacancy-text-section {
      background-color: ${({ theme }) => theme.primaryBackgroundColor};
    }

    .company-name {
      color: ${({ theme }) => theme.secondaryColor};
    }

    .location-city {
      color: ${({ theme }) => theme.primaryColor};
    }

    .vacancy-category,
    .vacancy-details {
      color: ${({ theme }) => theme.negativeTextColor};
    }
  }
`;

function formatCategory(category) {
  if (category == null) {
    return "";
  }

  // Remove non-alphabetic characters and replace spaces with hyphens
  return category.replace(/[^a-zA-Z ]/g, "").replace(/ /g, "-");
}

function GridVacancyItem(props) {
  const { vacancy, isLoading } = props;
  const { trackingStore, configStore } = useStores();
  const { config } = configStore;

  const { thumbnail, useComanyLogoAsThumbnail } = useContext(ComponentContext);

  const vacancyHref = {
    pathname: `/job/${vacancy.id}`,
    query: trackingStore.refererQuery,
  };
  let thumbnailObject = thumbnail || config?.logo;

  if (useComanyLogoAsThumbnail && vacancy?.company?.logoUrlFull) {
    thumbnailObject = { url: vacancy?.company?.logoUrlFull };
  }

  const styledVacancyClassName = classNames(
    "h-100 text-center d-flex flex-column align-items-center justify-content-center",
    {
      [formatCategory(vacancy?.category)]: vacancy?.category,
    }
  );

  return (
    <Link href={vacancyHref} passHref legacyBehavior>
      <StyledVacancy
        $withoutImage={isEmpty(thumbnailObject)}
        className={styledVacancyClassName}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {thumbnailObject && (
              <StyledImageWrapper className="pt-2 pb-4">
                <Image
                  alt={vacancy?.jobTitle}
                  height={thumbnailObject.height || 140}
                  layout="responsive"
                  priority
                  src={thumbnailObject.url}
                  width={thumbnailObject.width || 170}
                />
              </StyledImageWrapper>
            )}
            <div className="vacancy-text-section w-100 d-flex flex-column h-100 px-1">
              <h3 className="vacancy-category mt-2 mb-4 fw-bold text-uppercase">
                {vacancy?.jobTitle}
              </h3>
              <p className="location-city">{vacancy?.location?.city}</p>
              <p className="vacancy-details">
                {vacancy?.remuneration?.description}
                <br />
                {hoursTypeMap[vacancy?.hoursType]}
              </p>
              <p className="company-name mt-auto fw-bold">
                {vacancy?.company?.name}
              </p>
            </div>
          </>
        )}
      </StyledVacancy>
    </Link>
  );
}

export default observer(GridVacancyItem);

GridVacancyItem.propTypes = {
  isLoading: PropTypes.bool,
  vacancy: PropTypes.shape({
    category: PropTypes.string,
    company: PropTypes.shape({
      name: PropTypes.string,
    }),
    hoursType: PropTypes.string,
    id: PropTypes.number,
    jobTitle: PropTypes.string,
    location: PropTypes.shape({
      city: PropTypes.string,
    }),
    remuneration: PropTypes.shape({
      description: PropTypes.string,
    }),
  }),
};

GridVacancyItem.defaultProps = {
  isLoading: false,
  vacancy: {},
};
